/**
 * Shared pieces of the definition editor: the definition and its usage counts, writes that
 * dry-run first and ask before anything that touches data, and inline text editing.
 */
import { useState, type FormEvent } from "react";
import { Pencil } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DefinitionDetail, DefinitionUsage, FieldTemplate, Me, type FieldKind } from "@workcore/schemas";
import { z } from "zod";
import { api, get } from "../../api";
import { ErrorBox, toast } from "../../ui";
import { confirmImpact } from "../../confirm";

export const defKey = (slug: string, id: number) => ["ws", slug, "definition", id];

export function useDefinition(slug: string, id: number) {
  return useQuery({
    queryKey: [...defKey(slug, id), "detail"],
    queryFn: async () => DefinitionDetail.parse(await get(`/v1/ws/${slug}/definitions/${id}`, { include_hidden: "true" })),
  });
}

export function useUsage(slug: string, id: number) {
  return useQuery({ queryKey: [...defKey(slug, id), "usage"], queryFn: async () => DefinitionUsage.parse(await get(`/v1/ws/${slug}/definitions/${id}/usage`)) });
}

export function useFieldTemplates(slug: string) {
  return useQuery({ queryKey: ["ws", slug, "field-templates"], queryFn: async () => z.array(FieldTemplate).parse(await get(`/v1/ws/${slug}/field-templates`)) });
}

/** May the caller edit this definition? (The API decides; this only hides controls.) */
export function useCanEdit(slug: string, id: number): boolean {
  const me = useQuery({ queryKey: ["ws-me", slug], queryFn: async () => Me.parse(await get(`/v1/ws/${slug}/me`)) });
  const m = me.data;
  return !!m && (m.isSystemAdmin || m.roles.some((r) => r.role === "workflow_admin" && r.scopeId === id));
}

export interface Write {
  method: string;
  path: string;
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  /** what this does to existing data, shown for confirmation after the dry run passes; omit when nothing is touched */
  impact?: string;
}

/**
 * Every definition change: dry run first (rule 7), so a refused change fails before anything
 * happens; then, if it touches existing data, ask; then commit. Refreshes the definition.
 */
export function useDefinitionWrite(slug: string, id: number) {
  const q = useQueryClient();
  return useMutation({
    mutationFn: async (w: Write) => {
      const opts = { ...(w.body !== undefined ? { body: w.body } : {}), ...(w.query ? { query: w.query } : {}) };
      await api(w.method, w.path, { ...opts, dryRun: true });
      if (w.impact && !(await confirmImpact(w.impact))) return "cancelled";
      return api(w.method, w.path, opts);
    },
    onSuccess: (r) => r !== "cancelled" && toast("Saved"),
    onSettled: () => q.invalidateQueries({ queryKey: defKey(slug, id) }),
  });
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

export function InlineText({ value, onSave, disabled, label }: { value: string; onSave: (v: string) => void; disabled?: boolean; label: string }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value);
  if (!editing || disabled) {
    if (disabled) return <span className="inline-text">{value || <span className="muted">—</span>}</span>;
    // The value itself is the edit control; the pencil only shows on hover or focus.
    return (
      <button type="button" className="inline-value" title={`Edit ${label}`} aria-label={`Edit ${label}: ${value || "empty"}`} onClick={() => (setV(value), setEditing(true))}>
        {value || <span className="muted">—</span>}
        <Pencil size={12} strokeWidth={1.5} aria-hidden="true" />
      </button>
    );
  }
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setEditing(false);
    if (v.trim() && v !== value) onSave(v.trim());
  };
  return (
    <form autoComplete="off" className="inline-edit" onSubmit={submit}>
      <input aria-label={label} value={v} autoFocus onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Escape" && setEditing(false)} />
      <button type="submit">Save</button>
      <button type="button" className="secondary" onClick={() => setEditing(false)}>
        Cancel
      </button>
    </form>
  );
}

export function WriteError({ error }: { error: unknown }) {
  return <ErrorBox error={error} />;
}

/** Turn what someone typed into a value of the field's kind (for defaults). */
export function parseValue(kind: FieldKind, raw: string): unknown {
  const s = raw.trim();
  if (kind === "integer") return Number.parseInt(s, 10);
  if (kind === "decimal") return Number(s);
  if (kind === "boolean") return s === "true" || s === "yes" || s === "1";
  if (kind === "multi_select") return s.split(",").map((x) => x.trim()).filter(Boolean);
  if (kind === "user" || kind === "object_ref") return Number(s);
  return s;
}

/** Keys: lower-case letters, digits and underscores (the API is the authority). */
export const toKey = (s: string) => s.toLowerCase().replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "");
